import React from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="bg-black min-h-screen">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-6 py-24">
        {/* Back to Home Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">Terms of Service</h1>
          <p className="text-gray-400">Last updated: January 2025</p>
        </div>

        {/* Content */}
        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Agreement to Terms</h2>
            <p className="leading-relaxed">
              By accessing or using HireX8™ services, you agree to be bound by these Terms of Service 
              and all applicable laws and regulations. If you do not agree with any of these terms, you 
              are prohibited from using or accessing this site.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
            <p className="leading-relaxed">
              HireX8™ provides a platform for building and managing Human+AI teams to enhance productivity 
              and efficiency. Our service includes access to AI-powered tools, team management features, 
              and related functionalities designed to help organizations achieve their goals faster.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. User Accounts</h2>
            <p className="leading-relaxed mb-4">
              When you create an account with us, you must provide accurate, complete, and current 
              information at all times. You are responsible for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Maintaining the confidentiality of your account and password</li>
              <li>Restricting access to your computer and account</li>
              <li>All activities that occur under your account or password</li>
              <li>Notifying us immediately of any unauthorized access or security breach</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Acceptable Use</h2>
            <p className="leading-relaxed mb-4">
              You agree not to use HireX8™ services to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Violate any applicable local, state, national, or international law</li>
              <li>Transmit any material that is unlawful, harmful, threatening, abusive, or offensive</li>
              <li>Impersonate any person or entity or falsely state or misrepresent your affiliation</li>
              <li>Interfere with or disrupt the service or servers or networks connected to the service</li>
              <li>Use any robot, spider, or other automatic device to access the service</li>
              <li>Attempt to gain unauthorized access to any portion of the service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Intellectual Property</h2>
            <p className="leading-relaxed">
              The service and its original content, features, and functionality are and will remain the 
              exclusive property of HireX8™ and its licensors. The service is protected by copyright, 
              trademark, and other laws. Our trademarks and trade dress may not be used in connection 
              with any product or service without our prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. User Content</h2>
            <p className="leading-relaxed mb-4">
              Our service may allow you to post, link, store, share and otherwise make available certain 
              information, text, graphics, or other material. You are responsible for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>The content that you post on or through the service</li>
              <li>Ensuring you have the right to post such content</li>
              <li>Ensuring your content does not violate any third-party rights</li>
            </ul>
            <p className="leading-relaxed mt-4">
              By posting content, you grant us the right and license to use, modify, publicly perform, 
              publicly display, reproduce, and distribute such content on and through the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Payment Terms</h2>
            <p className="leading-relaxed mb-4">
              By engaging HireX8 ("Hx8") services, the Client agrees to the following payment terms:
            </p>
            
            <div className="space-y-6 ml-4">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">1. Payment Setup</h3>
                <p className="leading-relaxed">
                  Before work begins, the Client must provide valid payment information through Hx8's 
                  secure payment portal. Accepted methods include ACH and Credit Card, both processed 
                  via Stripe.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">2. Billing and Charges</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    <strong>Resource-Based Work:</strong> Hx8 tracks approved time logged by assigned 
                    resources. Clients are billed weekly based on actual hours worked at the applicable 
                    hourly rate(s).
                  </li>
                  <li>
                    <strong>Project-Based Work:</strong> For fixed-scope projects or sprint engagements, 
                    Clients are charged immediately upon sprint completion or milestone delivery.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">3. Automatic Payments</h3>
                <p className="leading-relaxed">
                  Invoices are automatically generated and charged to the payment method on file per the 
                  applicable billing schedule. The Client authorizes Hx8 to automatically charge the 
                  default payment method for all due amounts without additional notice.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">4. Failed Payments</h3>
                <p className="leading-relaxed">
                  In the event of a failed or declined payment, Hx8 may pause or suspend all work and 
                  access to resources until payment is successfully processed. Reinstatement may be 
                  subject to clearing outstanding balances.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">5. Non-Refundable Fees</h3>
                <p className="leading-relaxed">
                  All payments made to Hx8 are non-refundable, except where expressly stated otherwise 
                  in writing.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">6. Pricing Changes</h3>
                <p className="leading-relaxed">
                  Hx8 reserves the right to adjust its rates or pricing structure with reasonable 
                  advance notice. Such changes will not affect active projects already in progress 
                  unless mutually agreed upon.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Termination</h2>
            <p className="leading-relaxed">
              We may terminate or suspend your account immediately, without prior notice or liability, 
              for any reason whatsoever, including without limitation if you breach the Terms. Upon 
              termination, your right to use the service will immediately cease. All provisions of the 
              Terms which by their nature should survive termination shall survive termination.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Disclaimer of Warranties</h2>
            <p className="leading-relaxed">
              The service is provided on an "AS IS" and "AS AVAILABLE" basis. HireX8™ makes no 
              representations or warranties of any kind, express or implied, as to the operation of the 
              service or the information, content, or materials included on the service. You expressly 
              agree that your use of the service is at your sole risk.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Limitation of Liability</h2>
            <p className="leading-relaxed">
              In no event shall HireX8™, its directors, employees, or agents be liable for any indirect, 
              incidental, special, consequential, or punitive damages, including without limitation loss 
              of profits, data, use, goodwill, or other intangible losses, resulting from your access to 
              or use of or inability to access or use the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Indemnification</h2>
            <p className="leading-relaxed">
              You agree to defend, indemnify, and hold harmless HireX8™ and its licensors and licensees 
              from and against any claims, actions or demands, including reasonable legal fees, arising 
              or resulting from your breach of these Terms or your violation of any law or the rights of 
              a third party.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Governing Law</h2>
            <p className="leading-relaxed">
              These Terms shall be governed and construed in accordance with the laws of the jurisdiction 
              in which HireX8™ operates, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13. Changes to Terms</h2>
            <p className="leading-relaxed">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. 
              If a revision is material, we will try to provide at least 30 days' notice prior to any new 
              terms taking effect. What constitutes a material change will be determined at our sole 
              discretion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">14. Contact Us</h2>
            <p className="leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at{' '}
              <a href="mailto:legal@hirex8.com" className="text-orange-500 hover:text-orange-400">
                legal@hirex8.com
              </a>
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfService;
